import { useState, useEffect } from "react";
import { ChevronDown, Mail, Sparkles, Gem, Heart, MapPin } from "lucide-react";

const TALLY_CONTACT_ID = process.env.REACT_APP_TALLY_CONTACT_ID;
const TALLY_NEWSLETTER_ID = process.env.REACT_APP_TALLY_NEWSLETTER_ID;

const LOGO_STRASBOURGEOISES = `${process.env.PUBLIC_URL}/images/logo-strasbourgeoises.png`;
const LOGO_STRASS_BOURGEOISES = `${process.env.PUBLIC_URL}/images/logo-strass-bourgeoises.png`;

const FALLBACK_IMAGES = [
  { src: `${process.env.PUBLIC_URL}/images/gallery-01.jpg`, alt: "Sandales artisanales" },
  { src: `${process.env.PUBLIC_URL}/images/gallery-02.jpg`, alt: "Création bijoux" },
  { src: `${process.env.PUBLIC_URL}/images/gallery-03.jpg`, alt: "Sandales élégantes" },
  { src: `${process.env.PUBLIC_URL}/images/gallery-04.jpg`, alt: "Collection artisanale" },
  { src: `${process.env.PUBLIC_URL}/images/gallery-05.jpg`, alt: "Sandales fleuries" },
];

const HeroSection = () => {
  return (
    <section className="hero-section relative min-h-screen flex flex-col items-center justify-center px-6 md:px-12 lg:px-24 py-20">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#C17F7F]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 text-center max-w-5xl mx-auto">
        <p className="text-sm tracking-[0.3em] uppercase text-[#66605B] mb-8 animate-fade-in font-light">
          Sandales Artisanales · Héritage Français
        </p>

        <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 mb-12">
          <div className="logo-container animate-fade-in-up cursor-pointer" style={{ animationDelay: "0.1s", opacity: 0 }}>
            <img
              src={LOGO_STRASBOURGEOISES}
              alt="Les Strasbourgeoises"
              className="h-32 md:h-40 lg:h-48 w-auto object-contain"
            />
          </div>

          <div className="hidden md:block w-px h-32 bg-[#E6E1DB]" />

          <div className="logo-container animate-fade-in-up cursor-pointer" style={{ animationDelay: "0.2s", opacity: 0 }}>
            <img
              src={LOGO_STRASS_BOURGEOISES}
              alt="Les Strass Bourgeoises"
              className="h-32 md:h-40 lg:h-48 w-auto object-contain"
            />
          </div>
        </div>

        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1A1A1A] mb-6 animate-fade-in-up leading-tight" style={{ animationDelay: "0.3s", opacity: 0 }}>
          L'Art de la Sandale<br />
          <span className="text-[#C17F7F]">Faite Main</span>
        </h1>

        <p className="text-lg md:text-xl text-[#66605B] max-w-2xl mx-auto mb-12 animate-fade-in-up font-light leading-relaxed" style={{ animationDelay: "0.4s", opacity: 0 }}>
          Des créations uniques ornées de bijoux, alliant l'élégance du strass à l'authenticité de l'artisanat alsacien.
        </p>

        <div className="inline-flex items-center gap-3 bg-[#1A1A1A] text-white px-6 py-3 animate-fade-in-up cursor-pointer" style={{ animationDelay: "0.5s", opacity: 0 }}>
          <Sparkles className="w-4 h-4 text-[#D4AF37]" />
          <span className="text-xs tracking-[0.2em] uppercase">Ouverture Prochaine</span>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 scroll-indicator">
        <ChevronDown className="w-6 h-6 text-[#66605B]" />
      </div>
    </section>
  );
};

const BrandSection = () => {
  return (
    <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F1E8]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-sm tracking-[0.3em] uppercase text-[#66605B] mb-4">Nos Collections</p>
          <div className="gold-line mx-auto mb-6" />
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A]">
            Deux Univers, Une Passion
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          <div className="brand-card">
            <div className="flex items-center gap-4 mb-6">
              <MapPin className="w-5 h-5 text-[#C17F7F]" />
              <span className="text-sm tracking-[0.2em] uppercase text-[#66605B]">Collection Régionale</span>
            </div>
            <h3 className="font-serif text-2xl md:text-3xl text-[#1A1A1A] mb-4">Les Strasbourgeoises</h3>
            <p className="text-[#66605B] leading-relaxed mb-6">
              Inspirées par la beauté de Strasbourg et les traditions alsaciennes, ces sandales rendent hommage à notre patrimoine régional. Chaque paire raconte une histoire, celle de notre belle région.
            </p>
            <ul className="space-y-2 text-[#66605B]">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#C17F7F]" />
                Thèmes alsaciens authentiques
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#C17F7F]" />
                Ornements inspirés de la cathédrale
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#C17F7F]" />
                Fait main à Strasbourg
              </li>
            </ul>
          </div>

          <div className="brand-card">
            <div className="flex items-center gap-4 mb-6">
              <Gem className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-sm tracking-[0.2em] uppercase text-[#66605B]">Collection Luxe</span>
            </div>
            <h3 className="font-serif text-2xl md:text-3xl text-[#1A1A1A] mb-4">Les Strass Bourgeoises</h3>
            <p className="text-[#66605B] leading-relaxed mb-6">
              L'alliance parfaite entre élégance et originalité. Des sandales précieuses, ornées de strass et de bijoux raffinés pour sublimer chacun de vos pas avec sophistication.
            </p>
            <ul className="space-y-2 text-[#66605B]">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#D4AF37]" />
                Strass et cristaux de qualité
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#D4AF37]" />
                Design chic et sophistiqué
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#D4AF37]" />
                Finitions luxueuses
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

const GallerySection = () => {
  const [images, setImages] = useState(FALLBACK_IMAGES);
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/gallery.json?v=${Date.now()}`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data.images) && data.images.length > 0) {
          setImages(
            data.images.map((item) => ({
              src: `${process.env.PUBLIC_URL}/images/${item.file}`,
              alt: item.alt || "Création artisanale",
            }))
          );
        }
      })
      .catch(() => {});
  }, []);

  const handleImageError = (index) => {
    setImageErrors((prev) => ({ ...prev, [index]: true }));
  };

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-sm tracking-[0.3em] uppercase text-[#66605B] mb-4">Galerie</p>
          <div className="gold-line mx-auto mb-6" />
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] mb-6">
            Nos Créations
          </h2>
          <p className="text-[#66605B] max-w-2xl mx-auto leading-relaxed">
            Chaque paire est unique, confectionnée avec amour et attention aux détails.
            Découvrez quelques-unes de nos réalisations artisanales.
          </p>
        </div>

        <div className="masonry-grid">
          {images.map((image, index) => (
            <div
              key={index}
              className="gallery-item masonry-item aspect-square bg-[#F5F1E8] overflow-hidden"
            >
              {imageErrors[index] ? (
                <div className="image-placeholder w-full h-full flex flex-col items-center justify-center p-4 text-center">
                  <Gem className="w-8 h-8 text-[#C17F7F] mb-3" />
                  <span className="text-sm">{image.alt}</span>
                </div>
              ) : (
                <>
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                    onError={() => handleImageError(index)}
                    loading="lazy"
                  />
                  <div className="gallery-overlay" />
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CustomizationSection = () => {
  return (
    <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-[#1A1A1A] text-white">
      <div className="max-w-4xl mx-auto text-center">
        <Heart className="w-10 h-10 text-[#D4AF37] mx-auto mb-8" />
        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-6">
          Créations Sur Mesure
        </h2>
        <p className="text-lg md:text-xl text-white/70 mb-12 leading-relaxed">
          Vous rêvez d'une paire unique qui vous ressemble ? Nos sandales peuvent être personnalisées selon vos envies :
          choix des couleurs, thématiques spéciales, occasions particulières.
        </p>

        <div className="grid md:grid-cols-3 gap-8 text-left">
          <div className="border border-white/20 p-6">
            <div className="w-10 h-10 border border-[#D4AF37] flex items-center justify-center mb-4">
              <span className="text-[#D4AF37] font-serif text-xl">1</span>
            </div>
            <h3 className="font-serif text-xl mb-3">Choisissez</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Couleurs, motifs, ornements... Définissez votre style.
            </p>
          </div>

          <div className="border border-white/20 p-6">
            <div className="w-10 h-10 border border-[#D4AF37] flex items-center justify-center mb-4">
              <span className="text-[#D4AF37] font-serif text-xl">2</span>
            </div>
            <h3 className="font-serif text-xl mb-3">Créons</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Nous réalisons votre paire avec soin et passion.
            </p>
          </div>

          <div className="border border-white/20 p-6">
            <div className="w-10 h-10 border border-[#D4AF37] flex items-center justify-center mb-4">
              <span className="text-[#D4AF37] font-serif text-xl">3</span>
            </div>
            <h3 className="font-serif text-xl mb-3">Admirez</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Recevez une création unique, faite pour vous.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const NewsletterSection = () => {
  return (
    <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F1E8]">
      <div className="max-w-2xl mx-auto text-center">
        <Mail className="w-10 h-10 text-[#C17F7F] mx-auto mb-8" />
        <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">
          Soyez les Premières Informées
        </h2>
        <p className="text-[#66605B] mb-10 leading-relaxed">
          Inscrivez-vous pour recevoir en avant-première la date d'ouverture de notre boutique
          et découvrir nos collections exclusives.
        </p>

        <div className="max-w-md mx-auto">
          <iframe
            data-tally-src={`https://tally.so/embed/${TALLY_NEWSLETTER_ID}?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1`}
            loading="lazy"
            width="100%"
            height="160"
            frameBorder="0"
            marginHeight="0"
            marginWidth="0"
            title="Inscription newsletter"
          />
        </div>
      </div>
    </section>
  );
};

const ContactSection = () => {
  return (
    <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24">
      <div className="max-w-4xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <p className="text-sm tracking-[0.3em] uppercase text-[#66605B] mb-4">Contact</p>
            <div className="gold-line mb-6" />
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">
              Une Question ?
            </h2>
            <p className="text-[#66605B] mb-8 leading-relaxed">
              N'hésitez pas à nous contacter pour toute question concernant nos créations,
              les commandes personnalisées ou simplement pour dire bonjour.
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 border border-[#E6E1DB] flex items-center justify-center">
                  <Mail className="w-4 h-4 text-[#C17F7F]" />
                </div>
                <span className="text-[#66605B]">contact@les-strasbourgeoises.fr</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 border border-[#E6E1DB] flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-[#C17F7F]" />
                </div>
                <span className="text-[#66605B]">Strasbourg, Alsace</span>
              </div>
            </div>
          </div>

          <div>
            <iframe
              data-tally-src={`https://tally.so/embed/${TALLY_CONTACT_ID}?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1`}
              loading="lazy"
              width="100%"
              height="500"
              frameBorder="0"
              marginHeight="0"
              marginWidth="0"
              title="Formulaire de contact"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="py-12 px-6 md:px-12 lg:px-24 bg-[#1A1A1A] text-white">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-6">
            <img
              src={LOGO_STRASBOURGEOISES}
              alt="Les Strasbourgeoises"
              className="h-12 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
            />
            <img
              src={LOGO_STRASS_BOURGEOISES}
              alt="Les Strass Bourgeoises"
              className="h-12 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
            />
          </div>

          <p className="text-white/50 text-sm">
            © {currentYear} Les Strasbourgeoises & Les Strass Bourgeoises. Tous droits réservés.
          </p>
        </div>

        <div className="mt-8 pt-8 border-t border-white/10 text-center">
          <p className="text-white/40 text-xs tracking-wider uppercase">
            Sandales Artisanales · Fait Main en Alsace · Héritage Français
          </p>
        </div>
      </div>
    </footer>
  );
};

const LandingPage = () => {
  return (
    <main>
      <HeroSection />
      <BrandSection />
      <GallerySection />
      <CustomizationSection />
      <NewsletterSection />
      <ContactSection />
      <Footer />
    </main>
  );
};

export default LandingPage;
